import { Message } from "../model";
import { getStoreData, putStoreData, getAllStoreKeys } from "@/shared/lib";

export type SerializableTempMessage = Omit<Message, "files"> & {
  files?: undefined;
  filesBase64?: string[];
  err?: boolean;
  errFiles?: string[];
};

const STORE_NAME = "messages";

export const setTempMessagesDB = async (
  chatId: string,
  messages: SerializableTempMessage[]
) => {
  await putStoreData(STORE_NAME, chatId, messages);
};

export const setTempMessageDB = async (
  chatId: string,
  message: SerializableTempMessage
) => {
  const existing =
    (await getStoreData<SerializableTempMessage[]>(STORE_NAME, chatId)) || [];

  const index = existing.findIndex((m) => m._id === message._id);
  if (index >= 0) existing[index] = message;
  else existing.push(message);

  await putStoreData(STORE_NAME, chatId, existing);
};

export const getTempMessagesDB = async (): Promise<
  Record<string, SerializableTempMessage[]>
> => {
  const result: Record<string, SerializableTempMessage[]> = {};
  const allKeys = await getAllStoreKeys(STORE_NAME);

  for (const key of allKeys) {
    const data = await getStoreData<SerializableTempMessage[]>(STORE_NAME, key);
    if (data) result[String(key)] = data;
  }

  return result;
};

export const removeTempMessageDB = async (chatId: string, sentAt: number) => {
  const existing =
    (await getStoreData<SerializableTempMessage[]>(STORE_NAME, chatId)) || [];
  const filtered = existing.filter((m) => m.sentAt !== sentAt);
  await putStoreData(STORE_NAME, chatId, filtered);
};

export const updateTempMessageErrorDB = async (
  chatId: string,
  sentAt: number,
  err: boolean,
  errFiles?: string[]
): Promise<void> => {
  const existing =
    (await getStoreData<SerializableTempMessage[]>(STORE_NAME, chatId)) || [];

  const updated = existing.map((m) =>
    m.sentAt === sentAt ? { ...m, err, errFiles } : m
  );

  await putStoreData(STORE_NAME, chatId, updated);
};

export const getLastLocalMessage = async (
  chatId: string
): Promise<Message | null> => {
  const tempMessagesDB = await getTempMessagesDB();
  const tempMessages = tempMessagesDB[chatId] || [];

  if (tempMessages.length === 0) return null;

  const messages: Message[] = tempMessages.map((tm) => ({
    ...tm,
    err: true,
  }));

  return messages.reduce((latest, msg) => {
    const latestTime =
      typeof latest.createdAt === "string"
        ? new Date(latest.createdAt).getTime()
        : latest.createdAt;
    const msgTime =
      typeof msg.createdAt === "string"
        ? new Date(msg.createdAt).getTime()
        : msg.createdAt;
    return msgTime > latestTime ? msg : latest;
  }, messages[0]);
};
